import Image from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";

export const ResizableImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: "100%",
                renderHTML: (attributes) => ({
                    width: attributes.width,
                }),
            },
            height: {
                default: "auto",
                renderHTML: (attributes) => ({
                    height: attributes.height,
                }),
            },
        };
    },

    renderHTML({ HTMLAttributes, node }) {
        const align = node.attrs.textAlign;
        let style = HTMLAttributes.style || "";

        if (align === "center") {
            style += " display: block; margin-left: auto; margin-right: auto;";
        } else if (align === "right") {
            style += " float: right; margin-left: 1.5rem; margin-bottom: 1rem;";
        } else if (align === "left") {
            style += " float: left; margin-right: 1.5rem; margin-bottom: 1rem;";
        }

        style = style
            .replace(/text-align:\s*(center|right|left|justify);?/g, "")
            .trim();

        const finalAttributes = { ...HTMLAttributes };
        if (style) {
            finalAttributes.style = style;
        } else {
            delete finalAttributes.style;
        }

        return [
            "img",
            mergeAttributes(this.options.HTMLAttributes, finalAttributes),
        ];
    },

    addNodeView() {
        return ({ node, getPos, editor }) => {
            const container = document.createElement("div");
            container.style.position = "relative";
            container.style.lineHeight = "0";
            container.classList.add("resizable-image-container");

            const updateContainerAlignment = (align) => {
                // Reset styles
                container.style.display = "inline-block";
                container.style.float = "none";
                container.style.marginLeft = "0";
                container.style.marginRight = "0";
                container.style.marginBottom = "0";
                container.style.marginTop = "0";
                container.style.width = "auto";

                if (align === "center") {
                    container.style.display = "block";
                    container.style.marginLeft = "auto";
                    container.style.marginRight = "auto";
                    container.style.width = "fit-content";
                    container.style.marginBottom = "1rem";
                } else if (align === "right") {
                    container.style.float = "right";
                    container.style.marginLeft = "1.5rem";
                    container.style.marginBottom = "1rem";
                    container.style.display = "block";
                } else if (align === "left") {
                    container.style.float = "left";
                    container.style.marginRight = "1.5rem";
                    container.style.marginBottom = "1rem";
                    container.style.display = "block";
                }
            };

            updateContainerAlignment(node.attrs.textAlign);

            const img = document.createElement("img");
            img.setAttribute("src", node.attrs.src);
            img.style.width = node.attrs.width;
            img.style.height = node.attrs.height;
            img.style.display = "block";
            img.style.maxWidth = "100%";

            const handle = document.createElement("div");
            handle.style.position = "absolute";
            handle.style.right = "0";
            handle.style.bottom = "0";
            handle.style.width = "12px";
            handle.style.height = "12px";
            handle.style.backgroundColor = "#3b82f6";
            handle.style.cursor = "nwse-resize";
            handle.style.zIndex = "10";
            handle.style.borderRadius = "2px";
            handle.classList.add("resize-handle");

            container.appendChild(img);
            container.appendChild(handle);

            let isResizing = false;
            let startX, startWidth;

            handle.addEventListener("mousedown", (e) => {
                e.preventDefault();
                isResizing = true;
                startX = e.clientX;
                startWidth = img.offsetWidth;

                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
            });

            const onMouseMove = (e) => {
                if (!isResizing) return;
                const deltaX = e.clientX - startX;
                const newWidth = startWidth + deltaX;
                img.style.width = `${newWidth}px`;
            };

            const onMouseUp = () => {
                if (!isResizing) return;
                isResizing = false;
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);

                if (typeof getPos === "function") {
                    const width = img.style.width;
                    editor.view.dispatch(
                        editor.view.state.tr.setNodeMarkup(getPos(), null, {
                            ...node.attrs,
                            width,
                        })
                    );
                }
            };

            return {
                dom: container,
                update: (updatedNode) => {
                    if (updatedNode.type.name !== "image") return false;
                    img.setAttribute("src", updatedNode.attrs.src);
                    img.style.width = updatedNode.attrs.width;
                    img.style.height = updatedNode.attrs.height;
                    updateContainerAlignment(updatedNode.attrs.textAlign);
                    return true;
                },
            };
        };
    },
});
